import { Alert, MenuItem, Stack } from '@mui/material';
import { FormDialog, FormTextField, ValueBadge } from '../../../design-system';
import { locationTypeOptions, todayInSeoul, validDate, validDescriptor, validReason } from '../model/referenceData';
import type { ReferenceLocation, ReferenceLocationType } from '../model/types';

export type LocationDraft = {
  descriptor: string;
  locationType: ReferenceLocationType;
  countryCode: string;
  timeZone: string;
  effectiveDate: string;
  reason: string;
};

export type LocationEditor = {
  mode: 'create' | 'edit';
  item?: ReferenceLocation;
  draft: LocationDraft;
};

export function emptyLocationDraft(): LocationDraft {
  return {
    descriptor: '',
    locationType: 'OFFICE',
    countryCode: 'KR',
    timeZone: 'Asia/Seoul',
    effectiveDate: todayInSeoul(),
    reason: '',
  };
}

export function locationDraftValid(draft: LocationDraft, mode: LocationEditor['mode']) {
  return (
    validDescriptor(draft.descriptor) &&
    /^[A-Z]{2}$/.test(draft.countryCode.trim().toUpperCase()) &&
    draft.timeZone.trim().length > 0 &&
    draft.timeZone.trim().length <= 100 &&
    validDate(draft.effectiveDate) &&
    (mode === 'create' || draft.effectiveDate <= todayInSeoul()) &&
    validReason(draft.reason)
  );
}

type LocationEditorDialogProps = {
  editor?: LocationEditor;
  busy: boolean;
  editorError?: string;
  onClose: () => void;
  onSubmit: () => void;
  onChange: <K extends keyof LocationDraft>(field: K, value: LocationDraft[K]) => void;
};

export function LocationEditorDialog({
  editor,
  busy,
  editorError,
  onClose,
  onSubmit,
  onChange,
}: LocationEditorDialogProps) {
  return (
    <FormDialog
      open={Boolean(editor)}
      onClose={onClose}
      onSubmit={onSubmit}
      title={editor?.mode === 'create' ? '근무지 등록' : '근무지 수정'}
      cancelLabel="취소"
      submitLabel="변경사항 저장"
      busy={busy}
      busyLabel="저장 중…"
      submitDisabled={!editor || !locationDraftValid(editor.draft, editor.mode)}
      maxWidth="sm"
    >
      {editor ? (
        <Stack spacing={2}>
          {editorError ? (
            <Alert severity="error" role="alert">
              {editorError}
            </Alert>
          ) : null}
          <FormTextField
            required
            label="이름"
            labelSuffix={`${editor.draft.descriptor.length}/120`}
            value={editor.draft.descriptor}
            disabled={busy}
            error={editor.draft.descriptor.length > 0 && !validDescriptor(editor.draft.descriptor)}
            onChange={(event) => onChange('descriptor', event.target.value)}
            slotProps={{
              htmlInput: {
                maxLength: 120,
              },
            }}
          />
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={2}
            sx={{
              '& > *': {
                flex: 1,
                minWidth: 0,
              },
            }}
          >
            <FormTextField
              select
              required
              label="유형"
              value={editor.draft.locationType}
              disabled={busy}
              onChange={(event) => onChange('locationType', event.target.value as ReferenceLocationType)}
            >
              {locationTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <ValueBadge label={option.label} />
                </MenuItem>
              ))}
            </FormTextField>
            <FormTextField
              required
              label="국가 코드"
              labelSuffix={`${editor.draft.countryCode.length}/2`}
              value={editor.draft.countryCode}
              disabled={busy}
              error={!/^[A-Z]{2}$/.test(editor.draft.countryCode.trim().toUpperCase())}
              helperText="ISO 3166-1 alpha-2 코드 두 자리를 입력해 주세요."
              onChange={(event) => onChange('countryCode', event.target.value.toUpperCase())}
              slotProps={{
                htmlInput: {
                  maxLength: 2,
                },
              }}
            />
          </Stack>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={2}
            sx={{
              '& > *': {
                flex: 1,
                minWidth: 0,
              },
            }}
          >
            <FormTextField
              required
              label="시간대"
              labelSuffix={`${editor.draft.timeZone.length}/100`}
              value={editor.draft.timeZone}
              disabled={busy}
              error={!editor.draft.timeZone.trim() || editor.draft.timeZone.trim().length > 100}
              helperText="IANA 시간대 이름을 입력해 주세요. 예: Asia/Seoul"
              onChange={(event) => onChange('timeZone', event.target.value)}
              slotProps={{
                htmlInput: {
                  maxLength: 100,
                },
              }}
            />
            <FormTextField
              required
              type="date"
              label="적용일"
              value={editor.draft.effectiveDate}
              disabled={busy}
              error={
                !validDate(editor.draft.effectiveDate) ||
                (editor.mode === 'edit' && editor.draft.effectiveDate > todayInSeoul())
              }
              helperText={
                editor.mode === 'edit'
                  ? '근무지 속성 수정은 오늘 또는 이전 날짜에만 적용할 수 있습니다.'
                  : '근무지 사용을 시작할 날짜입니다. 미래 시행도 예약할 수 있습니다.'
              }
              onChange={(event) => onChange('effectiveDate', event.target.value)}
              slotProps={{
                htmlInput:
                  editor.mode === 'edit'
                    ? {
                        max: todayInSeoul(),
                      }
                    : undefined,
              }}
            />
          </Stack>
          <FormTextField
            required
            multiline
            minRows={3}
            label="변경 사유"
            labelSuffix={`${editor.draft.reason.length}/500`}
            value={editor.draft.reason}
            disabled={busy}
            error={editor.draft.reason.length > 0 && !validReason(editor.draft.reason)}
            helperText="감사 이력에 남길 사유를 500자 이하로 입력해 주세요."
            onChange={(event) => onChange('reason', event.target.value)}
            slotProps={{
              htmlInput: {
                maxLength: 500,
              },
            }}
          />
        </Stack>
      ) : null}
    </FormDialog>
  );
}
